import type { InputConfig } from "./config";
import type { InputSample, ThrowAction, InputListener } from "./types";
import { MAX_THROW_RATE } from "./types";

interface TouchState {
  startX: number;
  startY: number;
  startT: number;
}

export class ThrowDetector {
  private active = false;
  private touches = new Map<number, TouchState>();
  private listener: InputListener | null = null;
  private lastThrowTimes: number[] = [];
  private nowTick: () => number;
  private config: InputConfig;
  private buttonPointers = new Set<number>(); // pointers that started on buttons

  constructor(config: InputConfig, nowTick: () => number) {
    this.config = config;
    this.nowTick = nowTick;
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active) {
      this.touches.clear();
      this.buttonPointers.clear();
    }
  }

  onInput(listener: InputListener): void {
    this.listener = listener;
  }

  markButtonPointer(pointerId: number): void {
    this.buttonPointers.add(pointerId);
  }

  processSample(sample: InputSample): void {
    if (!this.active) return;

    const { pointerId, phase, x, y, t } = sample;

    // Ignore if this pointer started on a button
    if (this.buttonPointers.has(pointerId)) {
      if (phase === "end" || phase === "cancel") {
        this.buttonPointers.delete(pointerId);
      }
      return;
    }

    if (phase === "start") {
      this.touches.set(pointerId, {
        startX: x,
        startY: y,
        startT: t,
      });
    } else if (phase === "end") {
      const state = this.touches.get(pointerId);
      if (!state) return;

      const dx = x - state.startX;
      const dy = y - state.startY;
      const duration = t - state.startT;

      // Check if it's an upward swipe
      if (
        dy < 0 &&
        Math.abs(dy) >= this.config.throwMinDyPx &&
        duration <= this.config.throwMaxDurationMs
      ) {
        this.emitThrow(dx, dy, duration, t);
      }

      this.touches.delete(pointerId);
    } else if (phase === "cancel") {
      this.touches.delete(pointerId);
    }
  }

  private emitThrow(
    dx: number,
    dy: number,
    duration: number,
    time: number,
  ): void {
    // Apply rate cap
    const now = time / 1000;
    this.lastThrowTimes = this.lastThrowTimes.filter((t) => now - t < 1.0);

    if (this.lastThrowTimes.length >= MAX_THROW_RATE) {
      return; // Drop event due to rate cap
    }

    this.lastThrowTimes.push(now);

    // Determine lane based on angle
    const angle = (Math.atan2(dx, -dy) * 180) / Math.PI; // angle from vertical
    const centerThreshold = this.config.throwCenterAngleDeg;

    let lane: "left" | "center" | "right";
    if (Math.abs(angle) <= centerThreshold) {
      lane = "center";
    } else if (angle < 0) {
      lane = "left";
    } else {
      lane = "right";
    }

    // Calculate power (normalized velocity)
    const distance = Math.sqrt(dx * dx + dy * dy);
    const velocity = distance / (duration / 1000); // pixels per second
    const power = Math.min(1, velocity / 2000); // normalize to 0..1, cap at 2000 px/s

    if (this.listener) {
      const action: ThrowAction = {
        type: "throw",
        lane,
        power,
        tick: this.nowTick(),
      };
      this.listener(action);
    }
  }
}
